
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Search, Filter, SortAsc, User, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import type { Posit } from "@/types/posit";
import ReactMarkdown from "react-markdown";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useState, useMemo } from "react";
import PositListCard from "@/components/posit/PositListCard";

const PublicPosits = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [pricingFilter, setPricingFilter] = useState("all"); // all, free, paid
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const POSITS_PER_PAGE = 12;

  const { data: posits, isLoading, error } = useQuery({
    queryKey: ["public-posits", !!user],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from("posits")
          .select(`
            *,
            users!fk_posits_user_id (
              first_name,
              last_name,
              handle
            )
          `)
          .in('privacy', ['public', 'exclusive'])
          .order('is_pinned', { ascending: false })
          .order('pin_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (error) {
          // Check if it's a malformed ID error
          if (error.message?.includes('invalid input syntax for type uuid') || 
              error.message?.includes('William') ||
              error.code === '22P02') {
            // Return empty array to prevent app crash
            return [];
          }
          throw error;
        }
        
        return data as unknown as Posit[];
      } catch (err: any) {
                 // Additional safety net for any UUID-related errors
         if (err.message?.includes('William') || err.message?.includes('invalid input syntax')) {
           return [] as Posit[]; // Return empty array instead of crashing
         }
         throw err;
      }
    },
    retry: (failureCount, error: any) => {
      // Don't retry on UUID format errors
      if (error?.message?.includes('invalid input syntax') || 
          error?.message?.includes('William')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Fetch popular posits for the showcase section (only for global view)
  const { data: popularPosits } = useQuery({
    queryKey: ["popular-posits"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from("posits")
          .select(`
            *,
            users!inner!fk_posits_user_id (
              first_name,
              last_name,
              handle
            )
          `)
          .in('privacy', ['public', 'exclusive'])
          .not('users.handle', 'is', null)
          .eq('is_pinned', true)  // Only show pinned posits
          .order('pin_order', { ascending: true })  // Order by pin order
          .order('view_count', { ascending: false })  // Then by view count
          .limit(8); // Show up to 8 popular posits

        if (error) {
          // Check if it's a malformed ID error
          if (error.message?.includes('invalid input syntax for type uuid') || 
              error.message?.includes('William') ||
              error.code === '22P02') {
            return [];
          }
          return [];
        }
        
        return data as unknown as Posit[];
      } catch (err: any) {
        // Additional safety net for any UUID-related errors
        if (err.message?.includes('William') || err.message?.includes('invalid input syntax')) {
          return [] as Posit[];
        }
        throw err;
      }
    },
    enabled: true, // Always fetch popular posits
    retry: (failureCount, error: any) => {
      // Don't retry on UUID format errors
      if (error?.message?.includes('invalid input syntax') || 
          error?.message?.includes('William')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Filter and sort posits (for pagination calculation)
  const allFilteredPosits = useMemo(() => {
    if (!posits) return [];

    let filtered = posits.filter(posit => {
      // Search filter
      const matchesSearch = !searchQuery || 
        posit.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (posit.public_description && posit.public_description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (posit.summary_light && posit.summary_light.toLowerCase().includes(searchQuery.toLowerCase()));

      // Pricing filter
      const isPaid = posit.is_paid || (posit.is_monetized && posit.subscription_price && posit.subscription_price > 0);
      const matchesPricing = pricingFilter === "all" || 
        (pricingFilter === "free" && !isPaid) ||
        (pricingFilter === "paid" && isPaid);

      return matchesSearch && matchesPricing;
    });

    // Sort filtered results (pinned posits always come first, then by recent)
    filtered.sort((a, b) => {
      // First, sort by pinned status (pinned items first)
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      
      // If both are pinned, sort by pin_order
      if (a.is_pinned && b.is_pinned) {
        const orderA = a.pin_order || 0;
        const orderB = b.pin_order || 0;
        if (orderA !== orderB) return orderA - orderB;
      }
      
      // For non-pinned items or same pin_order, sort by recent
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return filtered;
  }, [posits, searchQuery, pricingFilter]);

  // Paginated results
  const paginatedPosits = useMemo(() => {
    const startIndex = (currentPage - 1) * POSITS_PER_PAGE;
    const endIndex = startIndex + POSITS_PER_PAGE;
    return allFilteredPosits.slice(startIndex, endIndex);
  }, [allFilteredPosits, currentPage, POSITS_PER_PAGE]);

  // Pagination info
  const totalPages = Math.ceil(allFilteredPosits.length / POSITS_PER_PAGE);
  const totalResults = allFilteredPosits.length;

  if (isLoading) {
    return (
      <DashboardLayout
        title='Explore Posits'
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  // Clear search function
  const clearSearch = () => {
    setSearchQuery("");
    setPricingFilter("all");
    setCurrentPage(1); // Reset to first page
  };

  const headerActions = null;

  return (
    <DashboardLayout
      title='Explore Posits'
      headerActions={headerActions}
      maxWidth="2xl"
    >
      {/* Search and Filter Controls */}
      <div className="mb-6 space-y-4 sticky top-0 z-10 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-2">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search posits..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Pricing Filter */}
          <Select value={pricingFilter} onValueChange={setPricingFilter}>
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue placeholder="Pricing" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Posits</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Clear Filters */}
          {(searchQuery || pricingFilter !== "all") && (
            <Button variant="outline" onClick={clearSearch} className="whitespace-nowrap">
              Clear Filters
            </Button>
          )}
        </div>
      </div>





      {!totalResults ? (
        <div className="text-center py-8 sm:py-12">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4">No Posits Found</h2>
          <p className="text-muted-foreground mb-4">
            {searchQuery || pricingFilter !== "all" 
              ? "Try adjusting your search or filters" 
              : "No public posits available"
            }
          </p>
          {(searchQuery || pricingFilter !== "all") && (
            <Button variant="outline" onClick={clearSearch}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {paginatedPosits.map((posit) => {
            const creatorName = posit.users 
              ? `${posit.users.first_name || ''} ${posit.users.last_name || ''}`.trim() || posit.users.handle || 'Unknown Creator'
              : 'Unknown Creator';
            const creatorHandle = posit.users?.handle;
            
            return (
              <PositListCard
                key={posit.id}
                posit={posit}
                creatorHandle={creatorHandle}
                creatorName={creatorName}
                showCreatorButton={false}
                showEditButton={false}
                showShareButton={false}
                showWikiButton={false}
                variant="compact"
              />
            );
                      })}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // Show first page, last page, current page, and pages around current
                  return page === 1 || 
                         page === totalPages || 
                         Math.abs(page - currentPage) <= 1;
                })
                .map((page, index, array) => {
                  // Add ellipsis if there's a gap
                  const prevPage = array[index - 1];
                  const showEllipsis = prevPage && page - prevPage > 1;
                  
                  return (
                    <div key={page} className="flex items-center gap-1">
                      {showEllipsis && (
                        <span className="px-2 text-muted-foreground">...</span>
                      )}
                      <Button
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className="w-8 h-8"
                      >
                        {page}
                      </Button>
                    </div>
                  );
                })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        )}
    </DashboardLayout>
  );
};

export default PublicPosits;
